/**
 * Cyberpunk neon lighting effects for UI elements.
 * Uses layered graphics with ADD blendMode for glow simulation.
 */
import * as Phaser from "phaser"
import { THEME } from "../core/theme"

// ── Neon color palette ──────────────────────────────────────────
export const NEON = THEME.neon

export type FilterableGameObject = Phaser.GameObjects.GameObject &
  Phaser.GameObjects.Components.Filters & {
    enableFilters(): Phaser.GameObjects.GameObject
  }

interface GlowFilterOptions {
  color: number
  outerStrength?: number
  innerStrength?: number
  scale?: number
  knockout?: boolean
  quality?: number
  distance?: number
}

interface VignetteFilterOptions {
  x?: number
  y?: number
  radius?: number
  strength?: number
  color?: number
  blendMode?: number
}

interface CameraFxOptions {
  blurQuality?: 0 | 1 | 2
  blurX?: number
  blurY?: number
  blurStrength?: number
  blurSteps?: number
  vignetteX?: number
  vignetteY?: number
  vignetteRadius?: number
  vignetteStrength?: number
  vignetteColor?: number
  vignetteBlendMode?: number
}

/** Phaser text shadow config for neon glow effect */
export function neonTextShadow(
  color: string,
  blur = 8,
  fill = true,
): Phaser.Types.GameObjects.Text.TextShadow {
  return { offsetX: 0, offsetY: 0, color, blur, stroke: false, fill }
}

/** Draw a neon-glowing horizontal line with soft glow behind it */
export function drawNeonLine(
  gfx: Phaser.GameObjects.Graphics,
  x1: number,
  y: number,
  x2: number,
  color: number,
  coreAlpha = 0.9,
  glowAlpha = 0.15,
  coreWidth = 2,
  glowWidth = 8,
): void {
  // Outer glow
  gfx.lineStyle(glowWidth, color, glowAlpha)
  gfx.lineBetween(x1, y, x2, y)
  // Inner glow
  gfx.lineStyle(glowWidth * 0.5, color, glowAlpha * 1.5)
  gfx.lineBetween(x1, y, x2, y)
  // Core
  gfx.lineStyle(coreWidth, color, coreAlpha)
  gfx.lineBetween(x1, y, x2, y)
}

/** Draw a neon-glowing rounded rect border */
export function drawNeonBorder(
  gfx: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  color: number,
  radius = 12,
  coreAlpha = 0.6,
  glowAlpha = 0.08,
): void {
  // Outer glow layer
  gfx.lineStyle(6, color, glowAlpha)
  gfx.strokeRoundedRect(x - 2, y - 2, w + 4, h + 4, radius + 2)
  // Mid glow layer
  gfx.lineStyle(3, color, glowAlpha * 2)
  gfx.strokeRoundedRect(x - 1, y - 1, w + 2, h + 2, radius + 1)
  // Core border
  gfx.lineStyle(1.5, color, coreAlpha)
  gfx.strokeRoundedRect(x, y, w, h, radius)
}

/**
 * Draw a neon-glowing rounded rect border using Phaser 4 native Glow filter.
 * More expensive than drawNeonBorder but produces smoother, higher quality results.
 */
export function drawFilterNeonBorder(
  gfx: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  color: number,
  radius = 12,
  alpha = 0.8,
  glowStrength = 6,
): Phaser.GameObjects.Graphics {
  gfx.lineStyle(2, color, alpha)
  gfx.strokeRoundedRect(x, y, w, h, radius)

  // Clear any previously accumulated glow filters before adding the new one.
  // Without this, every hover/out cycle stacks another Glow on top of the last,
  // since card.clear() only erases drawn geometry, not the filter list.
  gfx.enableFilters()
  ;(
    gfx.filters as unknown as { internal: { clear(): void } } | null
  )?.internal.clear()

  applyGlowFilter(gfx as unknown as FilterableGameObject, {
    color,
    outerStrength: glowStrength,
    innerStrength: 0,
    quality: 10,
    distance: 12,
  })

  return gfx
}

/** Create a subtle scanline overlay covering a rectangular area */
export function drawScanlines(
  scene: Phaser.Scene,
  x: number,
  y: number,
  w: number,
  h: number,
  alpha = 0.03,
  spacing = 3,
): Phaser.GameObjects.Graphics {
  const gfx = scene.add.graphics()
  gfx.fillStyle(THEME.colors.black, alpha)
  for (let ly = y; ly < y + h; ly += spacing) {
    gfx.fillRect(x, ly, w, 1)
  }
  return gfx
}

/** Create a cyberpunk perspective grid background */
export function drawNeonGrid(
  scene: Phaser.Scene,
  x: number,
  y: number,
  w: number,
  h: number,
  color: number = NEON.cyan,
  lineAlpha = 0.06,
  spacing = 40,
): Phaser.GameObjects.Graphics {
  const gfx = scene.add.graphics()

  // Horizontal lines with fade
  for (let ly = y; ly <= y + h; ly += spacing) {
    const progress = (ly - y) / h
    const a = lineAlpha * (0.3 + progress * 0.7) // stronger at bottom
    gfx.lineStyle(1, color, a)
    gfx.lineBetween(x, ly, x + w, ly)
  }

  // Vertical lines with perspective convergence
  const cx = x + w / 2
  const vanishY = y - h * 0.3
  const numLines = Math.ceil(w / spacing)
  for (let i = -numLines / 2; i <= numLines / 2; i++) {
    const bottomX = cx + i * spacing
    // Lines converge toward center-top
    const topX = cx + i * spacing * 0.4
    const a = lineAlpha * (1 - (Math.abs(i) / (numLines / 2)) * 0.5)
    gfx.lineStyle(1, color, a)
    gfx.lineBetween(bottomX, y + h, topX, vanishY)
  }

  return gfx
}

/** Create ambient neon glow circles (ADD blendMode) */
export function addAmbientNeonGlow(
  scene: Phaser.Scene,
  x: number,
  y: number,
  color: number,
  radius = 200,
  alpha = 0.06,
): Phaser.GameObjects.Graphics {
  const gfx = scene.add.graphics()
  gfx.fillStyle(color, alpha)
  gfx.fillCircle(x, y, radius)
  gfx.fillStyle(color, alpha * 0.5)
  gfx.fillCircle(x, y, radius * 1.5)
  gfx.setBlendMode(Phaser.BlendModes.ADD)
  return gfx
}

/** Apply a Phaser 4 glow filter to a Game Object. */
export function applyGlowFilter(
  gameObject: FilterableGameObject,
  {
    color,
    outerStrength = 4,
    innerStrength = 0,
    scale = 1,
    knockout = false,
    quality = 8,
    distance = 8,
  }: GlowFilterOptions,
  isExternal = false,
): Phaser.Filters.Glow {
  gameObject.enableFilters()
  const filters = gameObject.filters
  if (!filters) {
    throw new Error("Filters were not enabled on the target Game Object.")
  }

  const list = isExternal ? filters.external : filters.internal
  const glow = list.addGlow(
    color,
    outerStrength,
    innerStrength,
    scale,
    knockout,
    quality,
    distance,
  )
  glow.setPaddingOverride(null)
  return glow
}

/** Apply a Phaser 4 vignette filter to a Game Object or Camera. */
export function applyVignetteFilter(
  target: FilterableGameObject | Phaser.Cameras.Scene2D.Camera,
  {
    x = 0.5,
    y = 0.5,
    radius = 0.5,
    strength = 0.5,
    color = THEME.colors.black,
    blendMode = Phaser.BlendModes.NORMAL,
  }: VignetteFilterOptions = {},
  isExternal = true,
): Phaser.Filters.Vignette {
  if (!(target instanceof Phaser.Cameras.Scene2D.Camera)) {
    ;(target as FilterableGameObject).enableFilters()
  }

  const filters = (target as any)
    .filters as Phaser.Types.GameObjects.FiltersInternalExternal
  if (!filters) {
    throw new Error("Filters are not available on the target.")
  }

  const list = isExternal ? filters.external : filters.internal
  return list.addVignette(x, y, radius, strength, color, blendMode)
}

/**
 * Create a subtle Phaser 4 simplex-noise atmosphere layer.
 * This is intentionally low-frequency and low-contrast so it reads as
 * ambient haze, not TV static.
 */
export function applyNoiseAtmosphere(
  scene: Phaser.Scene,
  x: number,
  y: number,
  w: number,
  h: number,
  alpha = 0.1,
  power = 1.2,
): Phaser.GameObjects.NoiseSimplex2D | null {
  const addFactory = scene.add as Phaser.GameObjects.GameObjectFactory & {
    noisesimplex2d?: (
      config?:
        | string
        | Phaser.Types.GameObjects.NoiseSimplex2D.NoiseSimplex2DQuadConfig,
      x?: number,
      y?: number,
      width?: number,
      height?: number,
    ) => Phaser.GameObjects.NoiseSimplex2D
  }

  if (!addFactory.noisesimplex2d) {
    return null
  }

  const noise = addFactory.noisesimplex2d(
    {
      noiseCells: [7, 5],
      noisePeriod: [7, 5],
      noiseOffset: [Math.random() * 2, Math.random() * 2],
      noiseFlow: Math.random() * Math.PI * 2,
      noiseWarpAmount: 0.18,
      noiseIterations: 3,
      noiseWarpIterations: 2,
      noiseValueFactor: power * 0.28,
      noiseValueAdd: 0.14,
      noiseValuePower: 1.9,
      noiseColorStart: [0.05, 0.07, 0.12, 0],
      noiseColorEnd: [0.18, 0.42, 0.62, 0.11],
    },
    x,
    y,
    w,
    h,
  )

  const configuredNoise = noise as Phaser.GameObjects.NoiseSimplex2D & {
    alpha: number
    scrollFactorX: number
    scrollFactorY: number
    blendMode: Phaser.BlendModes | string | number
  }

  configuredNoise.alpha = alpha
  configuredNoise.scrollFactorX = 0
  configuredNoise.scrollFactorY = 0
  configuredNoise.blendMode = Phaser.BlendModes.NORMAL

  return noise
}

function getCameraFilters(
  camera: Phaser.Cameras.Scene2D.Camera | null | undefined,
): Phaser.Types.GameObjects.FiltersInternalExternal | null {
  const filters = (
    camera as
      | (Phaser.Cameras.Scene2D.Camera & {
          filters?: Phaser.Types.GameObjects.FiltersInternalExternal | null
        })
      | null
      | undefined
  )?.filters

  return filters ?? null
}

/** Remove all filters from a camera. */
export function clearCameraFx(
  camera: Phaser.Cameras.Scene2D.Camera | null | undefined,
): void {
  const filters = getCameraFilters(camera)
  if (!filters) {
    return
  }

  filters.internal.clear()
  filters.external.clear()
}

/** Apply a reusable modal blur + vignette treatment to a camera. */
export function applyModalCameraFx(
  camera: Phaser.Cameras.Scene2D.Camera,
  {
    blurQuality = 0,
    blurX = 2,
    blurY = 2,
    blurStrength = 1.25,
    blurSteps = 4,
    vignetteX = 0.5,
    vignetteY = 0.5,
    vignetteRadius = 0.58,
    vignetteStrength = 0.5,
    vignetteColor = THEME.colors.black,
    vignetteBlendMode = Phaser.BlendModes.NORMAL,
  }: CameraFxOptions = {},
): { blur: Phaser.Filters.Blur; vignette: Phaser.Filters.Vignette } {
  const filters = getCameraFilters(camera)
  if (!filters) {
    throw new Error("Camera filters are not available on the target camera.")
  }

  clearCameraFx(camera)

  const blur = filters.internal.addBlur(
    blurQuality,
    blurX,
    blurY,
    blurStrength,
    0xffffff,
    blurSteps,
  )
  const vignette = filters.external.addVignette(
    vignetteX,
    vignetteY,
    vignetteRadius,
    vignetteStrength,
    vignetteColor,
    vignetteBlendMode,
  )

  return { blur, vignette }
}

/** Apply a low-health warning vignette with chromatic intensity. */
export function applyLowHealthVignette(
  camera: Phaser.Cameras.Scene2D.Camera,
  intensity = 1.0,
): Phaser.Filters.Vignette {
  const filters = getCameraFilters(camera)
  if (!filters) throw new Error("Camera filters not available")

  const vignette = filters.external.addVignette(
    0.5,
    0.5,
    0.85 - intensity * 0.1,
    0.25 + intensity * 0.25,
    THEME.colors.danger,
    Phaser.BlendModes.MULTIPLY,
  )

  return vignette
}

/**
 * Apply a shimmering force-field filter effect to a Graphics object.
 * Uses Displacement or simple Glow pulse.
 */
export function applyForceFieldShimmer(
  gfx: FilterableGameObject,
  color = NEON.cyan,
): Phaser.Filters.Glow {
  gfx.enableFilters()
  const glow = gfx.filters?.internal.addGlow(color, 4, 2, 1, false, 8, 10)
  if (!glow) throw new Error("Failed to add glow filter")
  return glow
}

type SectorOverlayType =
  | "toxic"
  | "void"
  | "city"
  | "snow"
  | "forest"
  | "reactor"
  | "frozen-night"
  | "moon"

/** Create a sector-specific animated overlay with visual effects */
export function createSectorOverlay(
  scene: Phaser.Scene,
  type: SectorOverlayType | undefined,
  x: number,
  y: number,
  w: number,
  h: number,
): Phaser.GameObjects.Container | null {
  if (!type) return null
  const container = scene.add.container(x, y)

  if (type === "toxic") {
    const gfx = scene.add.graphics()
    gfx.fillStyle(THEME.colors.danger, 0.05)
    gfx.fillRect(0, 0, w, h)
    container.add(gfx)
  } else if (type === "void") {
    const gfx = scene.add.graphics()
    gfx.fillStyle(THEME.colors.purple, 0.08)
    gfx.fillRect(0, 0, w, h)
    container.add(gfx)
  } else if (type === "city") {
    const gfx = scene.add.graphics()
    gfx.lineStyle(1, NEON.cyan, 0.03)
    for (let ly = 0; ly < h; ly += 60) {
      gfx.lineBetween(0, ly, w, ly)
    }
    container.add(gfx)
  } else if (type === "snow") {
    const gfx = scene.add.graphics()
    gfx.fillStyle(THEME.colors.purple, 0.07)
    gfx.fillRect(0, 0, w, h)
    container.add(gfx)
  } else if (type === "frozen-night") {
    const haze = scene.add.graphics()
    haze.fillStyle(0x050814, 0.42)
    haze.fillRect(0, 0, w, h)
    haze.fillStyle(THEME.neon.cyan, 0.04)
    haze.fillRect(0, 0, w, h)
    container.add(haze)

    const scan = scene.add.graphics()
    scan.lineStyle(1, THEME.neon.cyan, 0.045)
    for (let ly = 0; ly < h; ly += 18) {
      scan.lineBetween(0, ly, w, ly)
    }
    container.add(scan)

    const glow = scene.add.graphics()
    glow.fillStyle(THEME.colors.purple, 0.03)
    glow.fillRect(0, 0, w, h)
    container.add(glow)
  } else if (type === "moon") {
    const haze = scene.add.graphics()
    haze.fillStyle(0x02040a, 0.2)
    haze.fillRect(0, 0, w, h)
    haze.fillStyle(0x8aa3ff, 0.02)
    haze.fillRect(0, 0, w, h)
    container.add(haze)

    const scan = scene.add.graphics()
    scan.lineStyle(1, 0xbdd0ff, 0.03)
    for (let ly = 0; ly < h; ly += 20) {
      scan.lineBetween(0, ly, w, ly)
    }
    container.add(scan)

    const glow = scene.add.graphics()
    glow.fillStyle(THEME.neon.cyan, 0.02)
    glow.fillRect(0, 0, w, h)
    container.add(glow)
  } else if (type === "forest") {
    const gfx = scene.add.graphics()
    gfx.fillStyle(THEME.colors.success, 0.03)
    gfx.fillRect(0, 0, w, h)
    container.add(gfx)
  } else if (type === "reactor") {
    const gfx = scene.add.graphics()
    gfx.fillStyle(THEME.colors.warning, 0.03)
    gfx.fillRect(0, 0, w, h)
    container.add(gfx)
  }

  return container
}

/** Draw decorative corner accents (cyberpunk bracket style) */
export function drawCornerAccents(
  gfx: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  color: number,
  size = 12,
  alpha = 0.4,
): void {
  gfx.lineStyle(1.5, color, alpha)

  // Top-left
  gfx.lineBetween(x, y + size, x, y)
  gfx.lineBetween(x, y, x + size, y)

  // Top-right
  gfx.lineBetween(x + w - size, y, x + w, y)
  gfx.lineBetween(x + w, y, x + w, y + size)

  // Bottom-left
  gfx.lineBetween(x, y + h - size, x, y + h)
  gfx.lineBetween(x, y + h, x + size, y + h)

  // Bottom-right
  gfx.lineBetween(x + w - size, y + h, x + w, y + h)
  gfx.lineBetween(x + w, y + h - size, x + w, y + h)
}

/** Apply a threshold pass to a Camera for high-contrast "neon burst" introduction. */
export function applyThresholdPass(
  camera: Phaser.Cameras.Scene2D.Camera,
  edge1 = 0.5,
  edge2 = 1.0,
): Phaser.Filters.Threshold {
  const filters = (camera as any)
    .filters as Phaser.Types.GameObjects.FiltersInternalExternal
  if (!filters) throw new Error("Camera filters not available")

  return filters.external.addThreshold(edge1, edge2)
}
